import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { IUser } from "../../../shared/models/User";
import "./ImportData.scss";
import dayjs from "dayjs"; // You can use dayjs for handling dates easily
import {
  differenceInMonths,
  differenceInWeeks,
  differenceInDays,
} from "date-fns";
// If you are only using AlertMessage in the component, no need to import AlertType
import AlertMessage, { AlertState } from "./alert-component/Alert";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { dateFormat_YY_MM_DD } from "../../shared/utils/utils";
import logo from "./logo513.png"; // adjust path as needed

import { useAppContext } from "../../../shared/functions/Context";
import { getBase64ImageFromURL } from "../../../shared/functions/scorecard-pdf/ImageDefinition";
import useIndividualScorecard from "../../../shared/hooks/useIndividualScorecard";
import { IScorecardMetadata } from "../../../shared/models/ScorecardMetadata";
import { log } from "console";
import { IMeasure } from "../../../shared/models/Measure";
import { getFinalRating } from "./BonusReportFunctions";
interface IJobGradeSalary {
  jobGrade: string;
  salary: number;
  periodStartDate?: string; // Added optional field for Period Start Date
  periodEndDate?: string; // Added optional field for Period End Date
}

interface IJobGradeSalary {
  jobGrade: string;
  salary: number;
}

interface IJobGradeSalary {
  jobGrade: string;
  salary: number;
}

interface IMatchedUser {
  user: IUser;
  salary: number;
  overallScore: number | null; // changed from number to number | null
  bonusPercentage: number;
  bonusAmount: number;
  periodStartDate?: string; // Added optional field for Period Start Date
  periodEndDate?: string; // Added optional field for Period End Date
  monthsWorked: number;
  weeksWorked: number;
  daysWorked: number;
  notQualified: boolean; // ✅ add this line
}

interface IJobGradeSalary {
  jobGrade: string;
  salary: number;
}

const ImportData: React.FC<{ users: IUser[] }> = ({ users }) => {
  const { store, api } = useAppContext();

  const user = store.auth.meJson;

  const [jobGradeSalaryData, setJobGradeSalaryData] = useState<
    IJobGradeSalary[]
  >([]);
  const [matchedUsers, setMatchedUsers] = useState<IMatchedUser[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null); // Track uploaded file
  // Define your state
  const [alert, setAlert] = useState<AlertState | null>(null);

  const [selectedTab, setSelectedTab] = useState("All");
  const [unmatchedJobGrades, setUnmatchedJobGrades] = useState<string[]>([]);
  const [missingSalaries, setMissingSalaries] = useState<string[]>([]);

  const [scorecards, setScorecards] = useState<
    Record<string, IScorecardMetadata | null>
  >({});
  const [measuresByUser, setMeasuresByUser] = useState<
    Record<string, IMeasure[]>
  >({});
  console.log("measuresByUser", measuresByUser);
  console.log("scorecards", scorecards);

  const image = `${process.env.PUBLIC_URL}/logo513.png`;

  // Helper function to get today's date as string

  const getTodayDate = () => dayjs().format("YYYY-MM-DD");

  // Step 1: File Upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file); // Set the uploaded file
      const fileExtension = file.name.split(".").pop();
      if (fileExtension === "csv") {
        importCSV(file);
      } else if (["xls", "xlsx"].includes(fileExtension || "")) {
        importExcel(file);
      } else {
        setAlert({
          type: "error",
          message:
            "Unsupported file format. Please upload a CSV or Excel file.",
        });
      }
    }
  };

  // Step 2: Process CSV
  const importCSV = (file: File) => {
    Papa.parse(file, {
      complete: (result) => {
        const unmatchedGrades: string[] = [];
        const missingSalaryGrades: string[] = [];
        const errors: string[] = [];

        const parsedData = result.data
          .map((row: any, index: number) => {
            const jobGrade = row["JOB GRADES"]?.toString().trim();
            const salaryStr = row["SALARIES"]
              ?.toString()
              .replace(/[^0-9.-]/g, "")
              .trim();
            const salary = parseFloat(salaryStr);
            const periodStartDate = row["Period Start Date"]?.toString().trim();
            const periodEndDate = row["Period End Date"]?.toString().trim();

            const isRowEmpty =
              !jobGrade && !salaryStr && !periodStartDate && !periodEndDate;

            if (isRowEmpty) return null; // skip empty rows

            if (!jobGrade || isNaN(salary) || salary <= 0) {
              errors.push(
                `Row ${index + 2}: Invalid data - Grade: "${
                  jobGrade || "MISSING"
                }", Salary: "${salaryStr || "MISSING"}"`
              );

              if (!jobGrade && !unmatchedGrades.includes(jobGrade)) {
                unmatchedGrades.push(jobGrade);
              }

              if (isNaN(salary) || salary <= 0) {
                missingSalaryGrades.push(row["JOB GRADES"]);
              }
            }

            return {
              jobGrade: jobGrade || "",
              salary: isNaN(salary) ? 0 : salary,
              periodStartDate,
              periodEndDate,
            };
          })
          .filter(Boolean) as IJobGradeSalary[];

        setJobGradeSalaryData(parsedData);

        if (errors.length > 0) {
          setAlert({
            type: "warning",
            message: `Some rows have errors:\n\n${errors.join(
              "\n"
            )}\n\nYou can choose to fix the file or continue with valid data.`,
          });
        }

        setUnmatchedJobGrades(unmatchedGrades);
        setMissingSalaries(missingSalaryGrades);
      },
      header: true,
      skipEmptyLines: true,
    });
  };
  // Step 3: Process Excel
  const importExcel = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      const rawData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      }) as any[];

      if (rawData.length > 1) {
        const headers = rawData[0] as string[];
        const dataRows = rawData.slice(1);

        const errors: string[] = [];

        const parsedData: IJobGradeSalary[] = dataRows
          .map((row, index) => {
            const grade = row[headers.indexOf("JOB GRADES")]?.toString().trim();
            const salaryStr = row[headers.indexOf("SALARIES")]
              ?.toString()
              .replace(/,/g, "")
              .trim();
            const salaryVal = parseFloat(salaryStr);
            const periodStartDate = row[headers.indexOf("Period Start Date")]
              ?.toString()
              .trim();
            const periodEndDate = row[headers.indexOf("Period End Date")]
              ?.toString()
              .trim();

            const isRowEmpty =
              !grade && !salaryStr && !periodStartDate && !periodEndDate;

            if (isRowEmpty) return null; // skip empty rows

            if (!grade || isNaN(salaryVal) || salaryVal <= 0) {
              errors.push(
                `Row ${index + 2}: Invalid data - Grade: "${
                  grade || "MISSING"
                }", Salary: "${salaryStr || "MISSING"}"`
              );
            }

            return {
              jobGrade: grade || "",
              salary: isNaN(salaryVal) ? 0 : salaryVal,
              periodStartDate,
              periodEndDate,
            };
          })
          .filter(Boolean) as IJobGradeSalary[];

        setJobGradeSalaryData(parsedData);

        if (errors.length > 0) {
          setAlert({
            type: "warning",
            message: `Some rows have errors:\n\n${errors.join(
              "\n"
            )}\n\nYou can choose to fix the file or continue with valid data.`,
          });
        }
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const processBonus = () => {
    const missingSalary: string[] = [];
    const unmatchedJobGrades: string[] = [];

    const matchedData: IMatchedUser[] = users.map((user) => {
      const jobGrade = user.jobGrade;
      const jobInfo = jobGrade
        ? jobGradeSalaryData.find((data: any) => data.jobGrade === jobGrade)
        : undefined;

      if (!jobInfo && jobGrade && !unmatchedJobGrades.includes(jobGrade)) {
        unmatchedJobGrades.push(jobGrade);
      }

      const salary = jobInfo?.salary ?? 0;

      if (salary === 0 && jobGrade && !missingSalary.includes(jobGrade)) {
        missingSalary.push(jobGrade);
      }

      const measures = measuresByUser[user.uid] ?? [];
      console.log("new measure", measures);

      const agreement = scorecards[user.uid] ?? null;
      // console.log("my  measures,",measures,agreement);

      const overallScore = agreement
        ? getFinalRating(measures, agreement)
        : null;
      console.log("score in here ", overallScore);

      const periodStartDate = user.createdAt || new Date().toISOString();
      const periodEndDate = new Date().toISOString();

      const monthsWorked = differenceInMonths(
        new Date(periodEndDate),
        new Date(periodStartDate)
      );
      const weeksWorked = differenceInWeeks(
        new Date(periodEndDate),
        new Date(periodStartDate)
      );
      const daysWorked = differenceInDays(
        new Date(periodEndDate),
        new Date(periodStartDate)
      );

      const qualifiesForBonus = monthsWorked >= 6;
      const bonusPercentage = qualifiesForBonus
        ? calculateBonusPercentage(overallScore ?? 0)
        : 0;
      const bonusAmount = (salary * bonusPercentage) / 100;

      return {
        user,
        salary,
        overallScore,
        bonusPercentage,
        bonusAmount,
        periodStartDate,
        periodEndDate,
        monthsWorked,
        weeksWorked,
        daysWorked,
        notQualified: !qualifiesForBonus,
      };
    });

    setMatchedUsers(matchedData);
    setUnmatchedJobGrades(unmatchedJobGrades);
    setMissingSalaries(missingSalary);

    if (unmatchedJobGrades.length > 0 || missingSalary.length > 0) {
      const issues: string[] = [];
      if (unmatchedJobGrades.length > 0)
        issues.push(`Unmatched Job Grades: ${unmatchedJobGrades.join(", ")}`);
      if (missingSalary.length > 0)
        issues.push(
          `Missing Salaries for Job Grades: ${missingSalary.join(", ")}`
        );

      setAlert({
        type: "warning",
        message: `There are some issues picked up:\n\n${issues.join("\n")}`,
        isMissingData: true,
      });
    }
  };

  useEffect(() => {
    const loadAllScorecards = async () => {
      const allScorecards: Record<string, IScorecardMetadata | null> = {};

      for (const user of users) {
        try {
          const result = await api.individualScorecard.getAll();
          const scorecard = store.individualScorecard.getItemById(user.uid);
          allScorecards[user.uid] = scorecard?.asJson ?? null;
        } catch (error) {
          console.error(`Failed to load scorecard for user ${user.uid}`, error);
          allScorecards[user.uid] = null;
        }
      }

      setScorecards(allScorecards);
    };

    if (users.length > 0) {
      loadAllScorecards();
    }
  }, [users, api.individualScorecard]);

  useEffect(() => {
    const loadAllMeasures = async () => {
      const allMeasures: Record<string, IMeasure[]> = {};

      try {
        await api.measure.getAll(); // Pulls all into store
        const all = store.measure.all.map((m) => m.asJson);

        for (const user of users) {
          allMeasures[user.uid] = all.filter((m) => m.uid === user.uid);
        }

        setMeasuresByUser(allMeasures); // <== this should only run once, after all users
      } catch (error) {
        console.error("Failed to load measures", error);

        const emptyMeasures: Record<string, IMeasure[]> = {};
        users.forEach((user) => (emptyMeasures[user.uid] = []));
        setMeasuresByUser(emptyMeasures);
      }
    };

    if (users.length > 0) {
      loadAllMeasures();
    }
  }, [users, api.measure]);

  const handleStartDateChange = (index: number, newDate: string) => {
    const updatedUsers = [...matchedUsers];
    const periodStartDate = new Date(newDate);
    const periodEndDate = new Date(); // Today

    const monthsWorked = differenceInMonths(periodEndDate, periodStartDate);
    const weeksWorked = differenceInWeeks(periodEndDate, periodStartDate);
    const daysWorked = differenceInDays(periodEndDate, periodStartDate);

    const qualifiesForBonus = monthsWorked >= 6;
    // const bonusPercentage = qualifiesForBonus && updatedUsers[index].overallScore !== null ? calculateBonusPercentage(updatedUsers[index].overallScore) : 0;
    const bonusPercentage =
      qualifiesForBonus && updatedUsers[index].overallScore !== null
        ? calculateBonusPercentage(updatedUsers[index].overallScore as number)
        : 0;

    const bonusAmount = (updatedUsers[index].salary * bonusPercentage) / 100;

    updatedUsers[index] = {
      ...updatedUsers[index],
      periodStartDate: periodStartDate.toLocaleDateString("en-GB", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "2-digit",
      }),
      monthsWorked,
      weeksWorked,
      daysWorked,
      notQualified: !qualifiesForBonus,
      bonusPercentage,
      bonusAmount,
    };

    setMatchedUsers(updatedUsers);
  };

  const calculateBonusPercentage = (score: number): number => {
    if (score >= 3.01 && score < 3.49) return 10;
    if (score >= 3.49 && score < 4.0) return 12;
    if (score >= 4.0 && score < 4.49) return 14;
    if (score >= 4.49 && score < 5.0) return 16;
    return 0; // Anything 3.00 and below or above 5.00
  };

  pdfMake.vfs = pdfFonts.pdfMake.vfs;
  const generateAndSendBonusPDF = async (email: string) => {
    const dataURL = await getBase64ImageFromURL(logo);
    try {
      // Construct the bonus report content
      const docDefinition: any = {
        content: [
          {
            width: "auto",
            alignment: "center",
            stack: [
              {
                image: dataURL,
                fit: [60, 60],
                alignment: "center",
                margin: [0, 0, 0, 5],
              },
              {
                table: {
                  headerRows: 1,
                  margin: [0, -20, 0, 0],
                  widths: ["auto", "auto"],
                  body: [
                    [
                      { text: "Vision", color: "black", fontSize: 7 },
                      {
                        text: " To be the leading electricity regulator in Africa",
                        color: "black",
                        fontSize: 7,
                      },
                    ],
                    [
                      {
                        text: "Mission: ",
                        style: "tableContent",
                      },
                      {
                        text: `${"To regulate the Namibian Electricity Supply Industry in a sustainable manner, in the interest of all stakeholders with regard to efficiency, affordability, safety and accessibility."}`,
                        style: "tableContent",
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: function (i: number, node: any) {
                    return i === 0 || i === node.table.body.length ? 1 : 0;
                  },
                  vLineWidth: function (i: number, node: any) {
                    return i === 0 || i === node.table.widths.length ? 1 : 0;
                  },
                  hLineColor: function (i: number, node: any) {
                    return "#004c98";
                  },
                  vLineColor: function (i: number, node: any) {
                    return "#004c98";
                  },
                },
              },

              {
                text: `Exported By: ${user?.displayName}`,
                fontSize: 7,
                bold: true,
                alignment: "center",
                margin: [0, 10, 0, 0],
              },
              {
                text: `Date: ${dateFormat_YY_MM_DD(Date.now())}`,
                fontSize: 7,
                bold: true,
                alignment: "center",
              },
            ],
          },

          {
            table: {
              headerRows: 1,
              widths: ["auto", "auto", "auto", "auto", "auto", "auto", "*"],
              body: [
                [
                  { text: "Employee Name", style: "tableHeader" },
                  { text: "Overall Score", style: "tableHeader" },
                  { text: "Bonus %", style: "tableHeader" },
                  { text: "Months worked during period", style: "tableHeader" },
                  { text: "Start Date", style: "tableHeader" },
                  { text: "Job Grade", style: "tableHeader" },
                  { text: "Bonus Amount", style: "tableHeader" },
                ],
                ...matchedUsers.map((item) => [
                  { text: item.user.displayName || "", style: "tableContent" },
                  { text: item.overallScore ?? "", style: "tableContent" },
                  {
                    text: `${item.bonusPercentage ?? 0}%`,
                    style: "tableContent",
                  },
                  { text: item.monthsWorked ?? "", style: "tableContent" },
                  { text: item.periodStartDate || "", style: "tableContent" },
                  { text: item.user.jobGrade || "", style: "tableContent" },
                  {
                    text: `N$ ${item.bonusAmount ?? 0}`,
                    style: "tableContent",
                  },
                ]),
              ],
            },

            layout: {
              hLineWidth: function (i: number, node: any) {
                return 0.5;
              },
              vLineWidth: function (i: number, node: any) {
                return 0.5;
              },
              hLineColor: function (i: number, node: any) {
                return "#004c98";
              },
              vLineColor: function (i: number, node: any) {
                return "#004c98";
              },
            },
            margin: [0, 5, 0, 20],
          },

          {
            text: `Date Generated: ${dateFormat_YY_MM_DD(Date.now())}`,
            margin: [3, 14, 0, 0],
            color: "black",
            fontSize: 7,
            bold: true,
          },
          {
            text: "Generated By: E-Performance Management system",
            margin: [3, 0, 0, 10],
            color: "black",
            fontSize: 7,
            bold: true,
          },
        ],
        styles: {
          period: {
            fonSize: 5,
            margin: [0, 10, 0, 5],
          },
          subheader: {
            fontSize: 8,
            bold: true,
            margin: [0, 10, 0, 5],
          },
          infoText: {
            fontSize: 7,
            margin: [0, 2, 0, 2],
          },
          tableHeader: {
            bold: true,
            fontSize: 8,
            color: "white",
            fillColor: "#004c98",
            alignment: "left",
          },
          tableContent: {
            fontSize: 7,
            alignment: "left",
          },
        },
      };

      const pdfDoc = pdfMake.createPdf(docDefinition);

      // Get Base64 encoded PDF content
      pdfDoc.getBase64(async (data) => {
        try {
          const htmlContent = `
            <body style="font-family: Arial, sans-serif; background-color: #f5f5f5; margin: 0; padding: 0;">
              <div style="max-width: 1200px; margin: 20px auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); padding: 20px; text-align: left;">
                <h2 style="color: #333333;">Bonus Report</h2>
                <p style="color: #666666; font-size: 14px;">Please find attached the bonus report for the respective users.</p>
                <p style="color: #666666; font-size: 14px;">If you have any questions, feel free to reach out.</p>
                <div style="margin-top: 10px; font-style: italic; color: #999999;">
                  <p style="font-size: 12px;">Best regards,<br />Your Team</p>
                </div>
              </div>
            </body>
          `;

          const to = email; // Update with recipient's email
          const from = "e-performance@ecb.org.na"; // Sender's email

          // Send PDF content to Cloud Function
          const response = await fetch(
            "https://sendemailsv2-p2xodpgboa-uc.a.run.app",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                pdfContent: data,
                htmlContent: htmlContent,
                to: to,
                from: from,
              }),
            }
          );

          if (response.ok) {
            setAlert({
              type: "success",
              message: "Bonus report sent successfully!",
            });
          } else {
            throw new Error("Failed to send bonus report");
          }
        } catch (error: unknown) {
          // Check if the error is an instance of Error
          if (error instanceof Error) {
            setAlert({
              type: "error",
              message: `Error sending bonus report: ${error.message}`,
            });
          } else {
            // In case it's not an instance of Error, handle accordingly
            setAlert({
              type: "error",
              message:
                "An unknown error occurred while sending the bonus report.",
            });
          }
        }
      });
    } catch (error: unknown) {
      // Check if the error is an instance of Error
      if (error instanceof Error) {
        setAlert({
          type: "error",
          message: `Error generating bonus report: ${error.message}`,
        });
      } else {
        // In case it's not an instance of Error, handle accordingly
        setAlert({
          type: "error",
          message:
            "An unknown error occurred while generating the bonus report.",
        });
      }
    }
  };

  const generateAndDownloadBonusPDF = async (e?: React.MouseEvent) => {
    e?.preventDefault?.(); // Optional safeguard

    const dataURL = await getBase64ImageFromURL(logo);
    try {
      const docDefinition: any = {
        content: [
          {
            width: "auto",
            alignment: "center",
            stack: [
              {
                image: dataURL,
                fit: [60, 60],
                alignment: "center",
                margin: [0, 0, 0, 5],
              },
              {
                table: {
                  headerRows: 1,
                  margin: [0, -20, 0, 0],
                  widths: ["auto", "auto"],
                  body: [
                    [
                      { text: "Vision", color: "black", fontSize: 7 },
                      {
                        text: " To be the leading electricity regulator in Africa",
                        color: "black",
                        fontSize: 7,
                      },
                    ],
                    [
                      {
                        text: "Mission: ",
                        style: "tableContent",
                      },
                      {
                        text: `${"To regulate the Namibian Electricity Supply Industry in a sustainable manner, in the interest of all stakeholders with regard to efficiency, affordability, safety and accessibility."}`,
                        style: "tableContent",
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: function (i: number, node: any) {
                    return i === 0 || i === node.table.body.length ? 1 : 0;
                  },
                  vLineWidth: function (i: number, node: any) {
                    return i === 0 || i === node.table.widths.length ? 1 : 0;
                  },
                  hLineColor: function (i: number, node: any) {
                    return "#004c98";
                  },
                  vLineColor: function (i: number, node: any) {
                    return "#004c98";
                  },
                },
              },

              {
                text: `Exported By: ${user?.displayName}`,
                fontSize: 7,
                bold: true,
                alignment: "center",
                margin: [0, 10, 0, 0],
              },
              {
                text: `Date: ${dateFormat_YY_MM_DD(Date.now())}`,
                fontSize: 7,
                bold: true,
                alignment: "center",
              },
            ],
          },

          {
            table: {
              headerRows: 1,
              widths: ["auto", "auto", "auto", "auto", "auto", "auto", "*"],
              body: [
                [
                  { text: "Employee Name", style: "tableHeader" },
                  { text: "Overall Score", style: "tableHeader" },
                  { text: "Bonus %", style: "tableHeader" },
                  { text: "Months worked during period", style: "tableHeader" },
                  { text: "Start Date", style: "tableHeader" },
                  { text: "Job Grade", style: "tableHeader" },
                  { text: "Bonus Amount", style: "tableHeader" },
                ],
                ...matchedUsers.map((item) => [
                  { text: item.user.displayName || "", style: "tableContent" },
                  { text: item.overallScore ?? "", style: "tableContent" },
                  {
                    text: `${item.bonusPercentage ?? 0}%`,
                    style: "tableContent",
                  },
                  { text: item.monthsWorked ?? "", style: "tableContent" },
                  { text: item.periodStartDate || "", style: "tableContent" },
                  { text: item.user.jobGrade || "", style: "tableContent" },
                  {
                    text: `N$ ${item.bonusAmount ?? 0}`,
                    style: "tableContent",
                  },
                ]),
              ],
            },

            layout: {
              hLineWidth: function (i: number, node: any) {
                return 0.5;
              },
              vLineWidth: function (i: number, node: any) {
                return 0.5;
              },
              hLineColor: function (i: number, node: any) {
                return "#004c98";
              },
              vLineColor: function (i: number, node: any) {
                return "#004c98";
              },
            },
            margin: [0, 5, 0, 20],
          },

          {
            text: `Date Generated: ${dateFormat_YY_MM_DD(Date.now())}`,
            margin: [3, 14, 0, 0],
            color: "black",
            fontSize: 7,
            bold: true,
          },
          {
            text: "Generated By: E-Performance Management system",
            margin: [3, 0, 0, 10],
            color: "black",
            fontSize: 7,
            bold: true,
          },
        ],
        styles: {
          period: {
            fonSize: 5,
            margin: [0, 10, 0, 5],
          },
          subheader: {
            fontSize: 8,
            bold: true,
            margin: [0, 10, 0, 5],
          },
          infoText: {
            fontSize: 7,
            margin: [0, 2, 0, 2],
          },
          tableHeader: {
            bold: true,
            fontSize: 8,
            color: "white",
            fillColor: "#004c98",
            alignment: "left",
          },
          tableContent: {
            fontSize: 7,
            alignment: "left",
          },
        },
      };

      const pdfDoc = pdfMake.createPdf(docDefinition);

      // Download the PDF
      pdfDoc.download("bonus_report.pdf");
    } catch (error: unknown) {
      // Handle error if any
      if (error instanceof Error) {
        setAlert({
          type: "error",
          message: `Error generating bonus report: ${error.message}`,
        });
      } else {
        setAlert({
          type: "error",
          message:
            "An unknown error occurred while generating the bonus report.",
        });
      }
    }
  };

  return (
    <div className="bonus-process-container">
      {alert && (
        <AlertMessage
          type={alert.type}
          message={alert.message}
          isMissingData={alert.isMissingData} // <-- You need this
          onClose={() => setAlert(null)}
        />
      )}

      {currentStep === 1 && (
        <div className="step step-1">
          <h4 className="step-title">
            <svg
              className="step-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path
                fill="currentColor"
                d="M19 3H5c-1.1 0-2 .9-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5c0-1.1-.9-2-2-2zM5 19V5h14v14H5zM12 17l-4-4h3V9h2v4h3l-4 4z"
              />
            </svg>
            Step 1: Import CSV or Excel File
          </h4>

          <p className="step-instructions">
            Please upload a CSV or Excel file containing your bonus data. Ensure
            the file includes the correct columns before proceeding.
          </p>

          <div className="file-upload-wrapper">
            <label htmlFor="file-upload" className="custom-upload-btn">
              {uploadedFile ? "📄 Change File" : "📁 Choose File"}
            </label>
            <input
              id="file-upload"
              type="file"
              accept=".csv, .xls, .xlsx"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            {uploadedFile && (
              <div className="uploaded-file-info">
                <span className="file-name">{uploadedFile.name}</span>
                <button
                  onClick={() => setUploadedFile(null)}
                  className="remove-file-btn"
                  title="Remove File"
                >
                  ✖
                </button>
              </div>
            )}
          </div>

          <button
            className="btn btn-primary next-btn"
            onClick={() => {
              setCurrentStep(2);
              processBonus();
            }}
            disabled={!uploadedFile}
          >
            Next: Process Bonus →
          </button>
        </div>
      )}

      {currentStep === 2 && (
        <div className="step step-2">
          <h3 className="step-title centered">📊 Step 2: Process Bonus</h3>

          {matchedUsers.length > 0 && (
            <>
              <h4 className="section-title centered">Bonus Report</h4>

              <div className="tabs">
                {[
                  "All",
                  "Unmatched Job Grades",
                  "Missing Salaries",
                  "Not qualified for Bonus",
                ].map((tab) => (
                  <button
                    key={tab}
                    className={`tab-btn ${selectedTab === tab ? "active" : ""}`}
                    onClick={() => setSelectedTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="table-wrapper">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Job Grade</th>
                      <th>Salary</th>
                      <th>Overall Score</th>
                      <th>Bonus %</th>
                      <th>Bonus Amount</th>
                      <th>Period Start Date</th>
                      <th>Period End Date</th>
                      <th>Months Worked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchedUsers
                      .filter((user) => {
                        if (selectedTab === "All") return true;
                        if (selectedTab === "Unmatched Job Grades")
                          return (
                            user.user.jobGrade &&
                            unmatchedJobGrades.includes(user.user.jobGrade)
                          );
                        if (selectedTab === "Missing Salaries")
                          return (
                            user.user.jobGrade &&
                            missingSalaries.includes(user.user.jobGrade)
                          );
                        if (selectedTab === "Not qualified for Bonus")
                          return user.notQualified;
                        return false;
                      })
                      .map((user, index) => (
                        <tr key={index}>
                          <td>{user.user.displayName}</td>
                          <td>{user.user.jobGrade}</td>
                          <td>${user.salary.toFixed(2)}</td>
                          <td>
                            {user.overallScore}
                            {user.notQualified && (
                              <span
                                className="warning-icon"
                                title="Not qualified for bonus"
                              >
                                ⚠️
                              </span>
                            )}
                          </td>
                          <td>{user.bonusPercentage}%</td>
                          <td>${user.bonusAmount.toFixed(2)}</td>
                          <td>
                            <input
                              type="date"
                              value={
                                user.periodStartDate
                                  ? new Date(user.periodStartDate)
                                      .toISOString()
                                      .split("T")[0]
                                  : ""
                              }
                              onChange={(e) =>
                                handleStartDateChange(index, e.target.value)
                              }
                            />
                          </td>
                          <td>{user.periodEndDate}</td>
                          <td>
                            {user.monthsWorked > 0
                              ? `${user.monthsWorked} months`
                              : user.weeksWorked > 0
                              ? `${user.weeksWorked} weeks`
                              : `${user.daysWorked} days`}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="button-group">
            <button
              className="btn btn-primary"
              onClick={() => setCurrentStep(3)}
              // disabled={
              //   unmatchedJobGrades.length > 0 || missingSalaries.length > 0
              // }
            >
              Next: Send Report →
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setCurrentStep(1);
                setUploadedFile(null);
                setJobGradeSalaryData([]);
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="step step-3">
          <h3 className="step-title">Step 3: Send Bonus Report</h3>

          <div className="form-group centered">
            <label htmlFor="email-input">Recipient Email Address</label>
            <input
              id="email-input"
              className="email-input"
              type="email"
              placeholder="e.g. hr@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="button-group">
            <button
              className="btn btn-download"
              onClick={(e) => generateAndDownloadBonusPDF(e)}
            >
              📥 Download Report
            </button>

            <button
              className="btn btn-primary"
              onClick={() => generateAndSendBonusPDF(email)}
              disabled={
                unmatchedJobGrades.length > 0 ||
                missingSalaries.length > 0 ||
                !email
              }
            >
              📤 Send Report
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => setCurrentStep(2)}
            >
              🔙 Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportData;